import { supabase } from '../lib/supabase';

// Add snake_case to camelCase conversion helper
const toCamelCase = (obj) => {
  if (!obj) return obj;
  
  if (Array.isArray(obj)) {
    return obj?.map(item => toCamelCase(item));
  }
  
  if (typeof obj === 'object' && obj !== null) {
    const camelObj = {};
    Object.keys(obj)?.forEach(key => {
      const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
      camelObj[camelKey] = toCamelCase(obj?.[key]);
    });
    return camelObj;
  }
  
  return obj;
};

// Add camelCase to snake_case conversion helper
const toSnakeCase = (obj) => {
  if (!obj) return obj;
  
  if (Array.isArray(obj)) {
    return obj?.map(item => toSnakeCase(item));
  }
  
  if (typeof obj === 'object' && obj !== null) {
    const snakeObj = {};
    Object.keys(obj)?.forEach(key => {
      const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
      snakeObj[snakeKey] = toSnakeCase(obj?.[key]);
    });
    return snakeObj;
  }
  
  return obj;
};

export const ticketService = {
  // Get all tickets (for users: their own, for agents: all)
  async getAll(filters = {}) {
    try {
      let query = supabase?.from('support_tickets')?.select(`
          *,
          user:user_id (id, full_name, email, avatar_url),
          assigned_agent:assigned_agent_id (id, full_name, email)
        `)?.order('created_at', { ascending: false });

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.priority) {
        query = query?.eq('priority', filters?.priority);
      }
      if (filters?.category) {
        query = query?.eq('category', filters?.category);
      }
      if (filters?.assignedToMe) {
        const { data: { user } } = await supabase?.auth?.getUser();
        query = query?.eq('assigned_agent_id', user?.id);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data?.map(ticket => ({
        id: ticket?.id,
        ticketNumber: ticket?.ticket_number,
        userId: ticket?.user_id,
        assignedAgentId: ticket?.assigned_agent_id,
        category: ticket?.category,
        priority: ticket?.priority,
        status: ticket?.status,
        subject: ticket?.subject,
        description: ticket?.description,
        tags: ticket?.tags || [],
        createdAt: ticket?.created_at,
        updatedAt: ticket?.updated_at,
        resolvedAt: ticket?.resolved_at,
        closedAt: ticket?.closed_at,
        firstResponseAt: ticket?.first_response_at,
        satisfactionRating: ticket?.satisfaction_rating,
        satisfactionComment: ticket?.satisfaction_comment,
        user: ticket?.user ? {
          id: ticket?.user?.id,
          fullName: ticket?.user?.full_name,
          email: ticket?.user?.email,
          avatarUrl: ticket?.user?.avatar_url
        } : null,
        assignedAgent: ticket?.assigned_agent ? {
          id: ticket?.assigned_agent?.id,
          fullName: ticket?.assigned_agent?.full_name,
          email: ticket?.assigned_agent?.email
        } : null
      }));
    } catch (error) {
      console.error('Error fetching tickets:', error);
      throw error;
    }
  },

  // Get single ticket with responses
  async getById(ticketId) {
    try {
      const { data: ticket, error: ticketError } = await supabase?.from('support_tickets')?.select(`
          *,
          user:user_id (id, full_name, email, avatar_url),
          assigned_agent:assigned_agent_id (id, full_name, email)
        `)?.eq('id', ticketId)?.single();

      if (ticketError) throw ticketError;

      const { data: responses, error: responsesError } = await supabase?.from('ticket_responses')?.select(`
          *,
          user:user_id (id, full_name, email, avatar_url)
        `)?.eq('ticket_id', ticketId)?.order('created_at', { ascending: true });

      if (responsesError) throw responsesError;

      return {
        id: ticket?.id,
        ticketNumber: ticket?.ticket_number,
        userId: ticket?.user_id,
        assignedAgentId: ticket?.assigned_agent_id,
        category: ticket?.category,
        priority: ticket?.priority,
        status: ticket?.status,
        subject: ticket?.subject,
        description: ticket?.description,
        tags: ticket?.tags || [],
        createdAt: ticket?.created_at,
        updatedAt: ticket?.updated_at,
        resolvedAt: ticket?.resolved_at,
        closedAt: ticket?.closed_at,
        firstResponseAt: ticket?.first_response_at,
        satisfactionRating: ticket?.satisfaction_rating,
        satisfactionComment: ticket?.satisfaction_comment,
        user: ticket?.user ? {
          id: ticket?.user?.id,
          fullName: ticket?.user?.full_name,
          email: ticket?.user?.email,
          avatarUrl: ticket?.user?.avatar_url
        } : null,
        assignedAgent: ticket?.assigned_agent ? {
          id: ticket?.assigned_agent?.id,
          fullName: ticket?.assigned_agent?.full_name,
          email: ticket?.assigned_agent?.email
        } : null,
        responses: responses?.map(response => ({
          id: response?.id,
          ticketId: response?.ticket_id,
          userId: response?.user_id,
          responseType: response?.response_type,
          content: response?.content,
          isInternal: response?.is_internal,
          attachments: response?.attachments || [],
          createdAt: response?.created_at,
          updatedAt: response?.updated_at,
          user: response?.user ? {
            id: response?.user?.id,
            fullName: response?.user?.full_name,
            email: response?.user?.email,
            avatarUrl: response?.user?.avatar_url
          } : null
        }))
      };
    } catch (error) {
      console.error('Error fetching ticket:', error);
      throw error;
    }
  },

  // Create new ticket
  async create(ticketData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('support_tickets')?.insert({
          user_id: user?.id,
          category: ticketData?.category,
          priority: ticketData?.priority || 'medium',
          subject: ticketData?.subject,
          description: ticketData?.description
        })?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        ticketNumber: data?.ticket_number,
        userId: data?.user_id,
        category: data?.category,
        priority: data?.priority,
        status: data?.status,
        subject: data?.subject,
        description: data?.description,
        tags: data?.tags || [],
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error creating ticket:', error);
      throw error;
    }
  },

  // Update ticket (agents only)
  async update(ticketId, updates) {
    try {
      const updateData = {};
      if (updates?.status) updateData.status = updates?.status;
      if (updates?.priority) updateData.priority = updates?.priority;
      if (updates?.category) updateData.category = updates?.category;
      if (updates?.assignedAgentId !== undefined) updateData.assigned_agent_id = updates?.assignedAgentId;

      const { data, error } = await supabase?.from('support_tickets')?.update(updateData)?.eq('id', ticketId)?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        ticketNumber: data?.ticket_number,
        status: data?.status,
        priority: data?.priority,
        category: data?.category,
        assignedAgentId: data?.assigned_agent_id,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating ticket:', error);
      throw error;
    }
  },

  // Add response to ticket
  async addResponse(ticketId, content, isInternal = false) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('ticket_responses')?.insert({
          ticket_id: ticketId,
          user_id: user?.id,
          response_type: 'agent_response',
          content: content,
          is_internal: isInternal
        })?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        ticketId: data?.ticket_id,
        userId: data?.user_id,
        responseType: data?.response_type,
        content: data?.content,
        isInternal: data?.is_internal,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error adding response:', error);
      throw error;
    }
  },

  // Get ticket statistics
  async getStatistics() {
    try {
      const { data, error } = await supabase?.from('support_tickets')?.select('status, priority, category');

      if (error) throw error;

      const stats = {
        total: data?.length,
        byStatus: {},
        byPriority: {},
        byCategory: {}
      };

      data?.forEach(ticket => {
        stats.byStatus[ticket.status] = (stats?.byStatus?.[ticket?.status] || 0) + 1;
        stats.byPriority[ticket.priority] = (stats?.byPriority?.[ticket?.priority] || 0) + 1;
        stats.byCategory[ticket.category] = (stats?.byCategory?.[ticket?.category] || 0) + 1;
      });

      return stats;
    } catch (error) {
      console.error('Error fetching statistics:', error);
      throw error;
    }
  },

  // Get ticket templates
  async getTemplates(category = null) {
    try {
      let query = supabase?.from('ticket_templates')?.select('*')?.eq('is_active', true)?.order('title');

      if (category) {
        query = query?.eq('category', category);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data?.map(template => ({
        id: template?.id,
        title: template?.title,
        content: template?.content,
        category: template?.category,
        isActive: template?.is_active,
        createdBy: template?.created_by,
        createdAt: template?.created_at
      }));
    } catch (error) {
      console.error('Error fetching templates:', error);
      throw error;
    }
  },

  // Assign ticket to agent
  async assignTicket(ticketId, agentId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error: updateError } = await supabase?.from('support_tickets')?.update({ assigned_agent_id: agentId })?.eq('id', ticketId);

      if (updateError) throw updateError;

      const { error: assignmentError } = await supabase?.from('ticket_assignments')?.insert({
          ticket_id: ticketId,
          agent_id: agentId,
          assigned_by: user?.id
        });

      if (assignmentError) throw assignmentError;

      return { success: true };
    } catch (error) {
      console.error('Error assigning ticket:', error);
      throw error;
    }
  },

  // Get user's own tickets
  async getUserTickets() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('support_tickets')
        ?.select(`
          *,
          assigned_agent:assigned_agent_id (id, full_name, email)
        `)
        ?.eq('user_id', user?.id)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(ticket => ({
        id: ticket?.id,
        ticketNumber: ticket?.ticket_number,
        userId: ticket?.user_id,
        assignedAgentId: ticket?.assigned_agent_id,
        category: ticket?.category,
        priority: ticket?.priority,
        status: ticket?.status,
        subject: ticket?.subject,
        description: ticket?.description,
        createdAt: ticket?.created_at,
        updatedAt: ticket?.updated_at,
        resolvedAt: ticket?.resolved_at,
        closedAt: ticket?.closed_at,
        assignedAgent: ticket?.assigned_agent ? {
          id: ticket?.assigned_agent?.id,
          fullName: ticket?.assigned_agent?.full_name,
          email: ticket?.assigned_agent?.email
        } : null
      }));
    } catch (error) {
      console.error('Error fetching user tickets:', error);
      throw error;
    }
  },

  // Get messages for a ticket
  async getMessages(ticketId) {
    try {
      const { data, error } = await supabase
        ?.from('ticket_messages')
        ?.select(`
          *,
          sender:sender_id (id, full_name, email, avatar_url, role)
        `)
        ?.eq('ticket_id', ticketId)
        ?.order('created_at', { ascending: true });

      if (error) throw error;

      return data?.map(message => ({
        id: message?.id,
        ticketId: message?.ticket_id,
        senderId: message?.sender_id,
        message: message?.message,
        isInternalNote: message?.is_internal_note,
        createdAt: message?.created_at,
        senderName: message?.sender?.full_name,
        senderEmail: message?.sender?.email,
        senderAvatar: message?.sender?.avatar_url,
        senderRole: message?.sender?.role
      }));
    } catch (error) {
      console.error('Error fetching ticket messages:', error);
      throw error;
    }
  },

  // Add message to ticket
  async addMessage(ticketId, messageText) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('ticket_messages')
        ?.insert({
          ticket_id: ticketId,
          sender_id: user?.id,
          message: messageText,
          is_internal_note: false
        })
        ?.select()
        ?.single();

      if (error) throw error;

      // Update ticket's updated_at timestamp
      await supabase
        ?.from('support_tickets')
        ?.update({ updated_at: new Date()?.toISOString() })
        ?.eq('id', ticketId);

      return {
        id: data?.id,
        ticketId: data?.ticket_id,
        senderId: data?.sender_id,
        message: data?.message,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error adding message:', error);
      throw error;
    }
  }
};